/* ============================================================
   VITALCASA CRM — Login
   Pantalla de inicio de sesión (siempre tema oscuro).
   Card negra con borde rainbow, glow fuerte, logo real.
   ============================================================ */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [cargando, setCargando] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setCargando(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.error || "Email o contraseña incorrectos");
    } finally {
      setCargando(false);
    }
  };

  // ── Estilos ─────────────────────────────────────
  const s = {
    page: {
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "radial-gradient(ellipse at center, #0a0e17 0%, #030507 60%)",
      fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
      padding: 20,
    },

    // Wrapper para posicionar el glow detrás
    cardWrapper: {
      position: "relative",
      width: 420,
      maxWidth: "100%",
    },

    // Glow fuerte detrás de la card — pseudo-element via div
    glowLayer: {
  position: "absolute",
  top: -40,
  left: -40,
  right: -40,
  bottom: -40,
  borderRadius: 40,
  background: "linear-gradient(135deg, #06b6d4, #3b82f6, #8b5cf6, #ec4899, #f59e0b)",
  opacity: 0.18,
  filter: "blur(60px)",
  zIndex: 0,
  pointerEvents: "none",
},

    card: {
      position: "relative",
      zIndex: 1,
      background: "#0a0e14",
      borderRadius: 16,
      border: "1px solid rgba(255,255,255,0.06)",
      boxShadow: "0 0 60px 15px rgba(255,255,255,0.20), 0 0 120px 40px rgba(255,255,255,0.12), 0 0 200px 80px rgba(255,255,255,0.06)",
      overflow: "visible",
    },

    inner: {
      borderRadius: 16,
      overflow: "hidden",
    },

    // Rainbow accent bar
    accent: {
      height: 3,
      background: "linear-gradient(90deg, #06b6d4, #3b82f6, #8b5cf6, #ec4899, #f59e0b)",
    },

    // Logo area
    logoArea: {
      padding: "32px 40px 0",
      textAlign: "center",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },

    logoImg: {
      height: 64,
      objectFit: "contain",
      marginBottom: 8,
    },

    subtitle: {
      fontSize: 13,
      color: "#6b7280",
      fontWeight: 500,
      marginTop: 2,
      letterSpacing: "0.02em",
    },

    // Divider
    divider: {
      height: 1,
      background: "linear-gradient(90deg, transparent, #1a1f2e, transparent)",
      margin: "24px 40px 0",
    },

    // Form
    form: {
      padding: "24px 40px 0",
    },

    field: {
      marginBottom: 18,
    },

    label: {
      display: "block",
      fontSize: 10.5,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      color: "#6b7280",
      marginBottom: 8,
    },

    input: (focused) => ({
      width: "100%",
      padding: "13px 16px",
      borderRadius: 10,
      border: `2px solid ${focused ? "#2563eb" : "#171c28"}`,
      background: "#080b11",
      color: "#e2e8f0",
      fontSize: 14,
      fontWeight: 500,
      fontFamily: "inherit",
      outline: "none",
      transition: "border-color 0.2s, box-shadow 0.2s",
      boxShadow: focused ? "0 0 0 3px rgba(37,99,235,0.15)" : "none",
      boxSizing: "border-box",
    }),

    // Remember row
    optionsRow: {
      display: "flex",
      alignItems: "center",
      marginBottom: 24,
      marginTop: 2,
    },

    rememberLabel: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 12.5,
      color: "#6b7280",
      fontWeight: 500,
      cursor: "pointer",
      userSelect: "none",
    },

    checkbox: (checked) => ({
      width: 18,
      height: 18,
      borderRadius: 5,
      border: `2px solid ${checked ? "#2563eb" : "#374151"}`,
      background: checked ? "#2563eb" : "transparent",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 11,
      color: "#fff",
      cursor: "pointer",
      transition: "all 0.15s ease",
      flexShrink: 0,
    }),

    // Login button
    btnLogin: {
      width: "100%",
      padding: 14,
      borderRadius: 10,
      border: "none",
      background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
      color: "#ffffff",
      fontSize: 14.5,
      fontWeight: 700,
      cursor: cargando ? "wait" : "pointer",
      fontFamily: "inherit",
      boxShadow: "0 4px 14px rgba(37,99,235,0.4)",
      letterSpacing: "0.01em",
      transition: "all 0.2s",
      opacity: cargando ? 0.7 : 1,
    },

    // Error
    error: {
      background: "rgba(239,68,68,0.1)",
      border: "1px solid rgba(239,68,68,0.3)",
      color: "#ef4444",
      fontSize: 12.5,
      fontWeight: 500,
      padding: "10px 14px",
      borderRadius: 8,
      marginBottom: 16,
    },

    spacer: {
      height: 32,
    },

    version: {
      position: "absolute",
      bottom: -32,
      left: "50%",
      transform: "translateX(-50%)",
      fontSize: 10,
      color: "rgba(255,255,255,0.12)",
      fontWeight: 500,
      letterSpacing: "0.05em",
      whiteSpace: "nowrap",
    },
  };

  return (
    <div style={s.page}>
      <div style={s.cardWrapper}>
        {/* Glow rainbow detrás */}
        <div style={s.glowLayer} />

        <div style={s.card}>
          <div style={s.inner}>
            <div style={s.accent} />

            {/* Logo */}
            <div style={s.logoArea}>
              <img src="/logo-vitalcasa.png" alt="Vitalcasa" style={s.logoImg} />
              <div style={s.subtitle}>CRM Inmobiliario</div>
            </div>

            <div style={s.divider} />

            {/* Form */}
            <form style={s.form} onSubmit={handleSubmit}>
              <div style={s.field}>
                <label style={s.label}>Email</label>
                <input
                  style={s.input(focusedField === "email")}
                  type="email"
                  placeholder="juan@vitalcasa.es"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setFocusedField("email")}
                  onBlur={() => setFocusedField(null)}
                  required
                  autoComplete="email"
                />
              </div>

              <div style={s.field}>
                <label style={s.label}>Contraseña</label>
                <input
                  style={s.input(focusedField === "password")}
                  type="password"
                  placeholder="Introduce tu contraseña"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setFocusedField("password")}
                  onBlur={() => setFocusedField(null)}
                  required
                  autoComplete="current-password"
                />
              </div>

              {error && <div style={s.error}>{error}</div>}

              <div style={s.optionsRow}>
                <label style={s.rememberLabel} onClick={() => setRemember(!remember)}>
                  <span style={s.checkbox(remember)}>
                    {remember && "✓"}
                  </span>
                  Recordarme
                </label>
              </div>

              <button type="submit" style={s.btnLogin} disabled={cargando}>
                {cargando ? "Iniciando sesión..." : "Iniciar sesión"}
              </button>
            </form>

            <div style={s.spacer} />
          </div>

          <div style={s.version}>VitalCasa CRM v1.0 · © 2026</div>
        </div>
      </div>

      <style>{`
        input::placeholder {
          color: #4b5563 !important;
          opacity: 1;
        }
      `}</style>
    </div>
  );
}
